import { useCallback } from "react";

export enum ImageResolution {
  FULL = "full",
  SMALL = "small"
}

const RESOLUTION_SPECS: Record<
  ImageResolution,
  { width: number; height: number }
> = {
  [ImageResolution.FULL]: { width: Infinity, height: Infinity },
  [ImageResolution.SMALL]: { width: 320, height: 240 }
};

export function useImageResizer() {
  const resize = useCallback(
    async (file: File, resolution: ImageResolution): Promise<Blob> => {
      const { width: maxWidth, height: maxHeight } =
        RESOLUTION_SPECS[resolution];
      if (maxWidth === Infinity && maxHeight === Infinity) return file;

      const imageBitmap = await createImageBitmap(file);
      let { width, height } = imageBitmap;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);

      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to resize image."));
        }, file.type);
      });
    },
    []
  );

  return { resize };
}
